import React from 'react';
import { ArrowRight, FileSpreadsheet, Users, Truck, TrendingUp, DollarSign } from 'lucide-react';

function App() {
  const features = [
    {
      icon: <FileSpreadsheet className="w-6 h-6 text-blue-500" />,
      title: "Automated Settlement Processing",
      description: "Built by contractors who know the pain of settlement sheets. Transform hours of processing into minutes with our drag & drop system"
    },
    {
      icon: <Users className="w-6 h-6 text-blue-500" />,
      title: "Driver Management",
      description: "Streamlined onboarding and document management designed specifically for P&D contractor needs"
    },
    {
      icon: <Truck className="w-6 h-6 text-blue-500" />,
      title: "Fleet Maintenance",
      description: "Track maintenance and export MMRs exactly how FedEx requires them - we know because we do it ourselves"
    },
    {
      icon: <TrendingUp className="w-6 h-6 text-blue-500" />,
      title: "Performance Analytics",
      description: "Get the exact insights contractors need to maximize route performance and profitability"
    },
    {
      icon: <DollarSign className="w-6 h-6 text-blue-500" />,
      title: "Custom Incentives",
      description: "Create driver incentive structures that work - tested and proven in our own routes"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm fixed w-full z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex-shrink-0 flex items-center">
              <span className="text-2xl font-bold text-blue-600">RouteFact</span>
            </div>
            <div className="flex space-x-4 items-center">
              <a 
                href="https://app-routefact.devs.bensa.us" 
                className="text-gray-600 hover:text-blue-600 font-semibold px-4 py-2"
              >
                Log In
              </a>
              <a 
                href="https://buy.stripe.com/eVa8yO06J7eT4wM7ss" 
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                Start Free Trial
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Add padding to account for fixed header */}
      <div className="pt-16">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 to-blue-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="pt-16 pb-20">
              <div className="text-center">
                <div className="mb-6 text-blue-200 text-lg font-semibold">
                  Built by FedEx Contractors, for FedEx Contractors
                </div>
                <h1 className="text-4xl tracking-tight font-extrabold text-white sm:text-5xl md:text-6xl">
                  <span className="block">Save 20+ Hours Weekly</span>
                  <span className="block text-blue-200">Managing Your FedEx Routes</span>
                </h1>
                <p className="mt-3 max-w-md mx-auto text-base text-blue-100 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
                  We're FedEx Ground P&D contractors who built the management platform we always wanted. Every feature is designed from real contractor experience to solve real contractor challenges.
                </p>
                <div className="mt-10 flex justify-center gap-4">
                  <a 
                    href="https://buy.stripe.com/eVa8yO06J7eT4wM7ss"
                    className="rounded-md px-8 py-3 bg-white text-blue-600 font-semibold hover:bg-blue-50 transition-colors"
                  >
                    Start Free Trial
                  </a>
                  <button className="rounded-md px-8 py-3 bg-blue-700 text-white font-semibold hover:bg-blue-600 transition-colors">
                    Schedule Demo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Social Proof Banner */}
        <div className="bg-blue-900 py-3">
          <div className="max-w-7xl mx-auto px-4">
            <p className="text-center text-blue-100">
              Trusted by contractors managing over 100+ routes across the United States
            </p>
          </div>
        </div>

        {/* Features Section */}
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900">Built By Contractors Who Get It</h2>
              <p className="mt-4 text-lg text-gray-600">Every feature is designed from thousands of hours of real contractor experience</p>
            </div>

            <div className="mt-16 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {features.map((feature, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg">
                    {feature.icon}
                  </div>
                  <h3 className="mt-4 text-xl font-semibold text-gray-900">{feature.title}</h3>
                  <p className="mt-2 text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Time Savings Stats Section */}
        <div className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600">20+</div>
                <p className="mt-2 text-gray-600">Hours saved per week</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600">90%</div>
                <p className="mt-2 text-gray-600">Faster settlement processing</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600">5+</div>
                <p className="mt-2 text-gray-600">Years of combined contractor experience</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-blue-900 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-white">Ready to Get Your Time Back?</h2>
              <p className="mt-4 text-xl text-blue-200">Join fellow contractors who trust RouteFact to run their business</p>
              <div className="mt-8 flex justify-center gap-4">
                <a 
                  href="https://buy.stripe.com/eVa8yO06J7eT4wM7ss"
                  className="rounded-md px-8 py-3 bg-white text-blue-900 font-semibold hover:bg-blue-50 transition-colors inline-flex items-center"
                >
                  Start Your Free Trial
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
                <button className="rounded-md px-8 py-3 bg-blue-700 text-white font-semibold hover:bg-blue-600 transition-colors">
                  Schedule Demo
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;